// ── Caja negra del lado cliente (escáner de puerta y TPV) ────────────────────
// REGLA NÚMERO UNO: esto no puede degradar lo que está midiendo. El escáner quedó
// en ~18% de ocupación de hilo y el TPV cobra plata: la telemetría no puede comerse
// eso ni agregar un milisegundo al camino crítico.
// Por eso:
//   · nunca se hace await de un reporte (fire-and-forget con keepalive)
//   · nunca se llama desde el loop de análisis (el latido lee un ref, 1 vez/minuto)
//   · los latidos NO se reintentan ni se encolan: son métricas, no datos
//   · los errores sí se encolan si no hay red, pero con TOPE, para no competir con
//     la cola de escaneos (que es la que sí importa) ni llenar el almacenamiento
// SEGUNDA REGLA: nada de datos personales. Se sanitiza acá y otra vez en el
// servidor (que no confía en el cliente).

const API = process.env.REACT_APP_API_URL || '';
const QKEY = 'tk_telemetry_errors';
const MAX_QUEUE = 50;          // tope de errores en cola: descarta los más viejos
const MAX_MSG = 300;

// Misma idea que en el backend: los QR y los tokens son credenciales, no van nunca.
const REDACT = [
  [/\b[A-Z]{2}-[0-9A-F]{6}\b/g, '[QR]'],
  [/\bTR-[0-9A-F]{6}\b/g, '[QR]'],
  [/(\/scan\/)[0-9a-z]{10}\b/gi, '$1[TOKEN]'],
  [/(\/public\/)[0-9a-z]{10}\b/gi, '$1[TOKEN]'],
  [/(\/(?:l|mesa)\/)[0-9A-Za-z-]{6,24}\b/g, '$1[CODE]'],
  [/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g, '[JWT]'],
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[EMAIL]'],
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[UUID]'],
  [/(authorization|bearer|api[-_]?key|password|secret|token)["'\s:=]+[^\s"',}]+/gi, '$1=[REDACTED]'],
  [/\b\d{22}\b/g, '[CBU]'],
  [/\+?\d{9,15}\b/g, '[TEL]'],
  [/\b\d{7,8}\b/g, '[DNI]'],
];

// Identificador ALEATORIO de esta instalación. Sirve para distinguir los teléfonos
// de una misma puerta, que comparten el mismo link de escaneo (sin esto los tres se
// veían como uno). Es random, no derivado del equipo: no es fingerprinting.
const DEV_KEY = 'tk_device_id';
export function deviceId() {
  try {
    let id = localStorage.getItem(DEV_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 6);
      localStorage.setItem(DEV_KEY, id);
    }
    return id;
  } catch { return null; }
}

function clean(msg) {
  let s = String(msg == null ? '' : msg);
  s = s.split('\n').slice(0, 2).join(' | ');
  s = s.replace(/\?[^\s)]*/g, '');
  for (const [re, rep] of REDACT) s = s.replace(re, rep);
  return s.slice(0, MAX_MSG);
}

function post(path, body) {
  try {
    // keepalive: si la pantalla cambia justo ahora, el reporte igual sale.
    return fetch(`${API}/api/public/telemetry/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    return Promise.reject(new Error('post'));
  }
}

function readQueue() {
  try { const a = JSON.parse(localStorage.getItem(QKEY) || '[]'); return Array.isArray(a) ? a : []; }
  catch { return []; }
}
function writeQueue(a) {
  try { localStorage.setItem(QKEY, JSON.stringify(a.slice(-MAX_QUEUE))); } catch {}
}

// Reporta un error. NUNCA lanza, NUNCA bloquea. Si no hay red, lo encola.
export function reportError({ token, source = 'scanner', kind = 'js_error', message, route, context }) {
  const payload = { token, source, device_id: deviceId(), kind: String(kind).slice(0, 40), message: clean(message), route: route ? clean(route) : null, context: context || null };
  // setTimeout(0): sale del stack del handler que lo llamó, así ni el escaneo ni el
  // cobro esperan por esto.
  setTimeout(() => {
    if (!navigator.onLine) { const q = readQueue(); q.push(payload); writeQueue(q); return; }
    post('error', payload).catch(() => { const q = readQueue(); q.push(payload); writeQueue(q); });
  }, 0);
}

// Envía los errores que quedaron en cola (al recuperar la red). Borra solo lo que
// confirmó el servidor.
export function flushErrors() {
  if (!navigator.onLine) return;
  const q = readQueue();
  if (!q.length) return;
  writeQueue([]);   // se saca de la cola para no duplicar; si falla, se re-encola
  q.forEach((p) => {
    post('error', p).catch(() => { const cur = readQueue(); cur.push(p); writeQueue(cur); });
  });
}

// Latido de métricas. Sin reintento ni cola a propósito: si se pierde uno, el
// siguiente va en un minuto.
export function reportHeartbeat({ token, source = 'scanner', metrics }) {
  post('heartbeat', { token, source, device_id: deviceId(), metrics: metrics || {} }).catch(() => {});
}

// ── TPV ──
// El TPV sí tiene sesión propia, así que reporta por un endpoint autenticado: la org
// la resuelve el servidor del token verificado. Mismas reglas: fire-and-forget y
// nunca en el camino del cobro.
function postTpv(headers, body) {
  try {
    return fetch(`${API}/api/tpv/telemetry`, {
      method: 'POST', headers, body: JSON.stringify(body), keepalive: true,
    });
  } catch { return Promise.reject(new Error('post')); }
}

// `device_id` va también en los ERRORES, no solo en el latido. Sin él, trece fallas de
// impresión en una noche no se pueden atribuir: no se sabe si es una impresora para
// cambiar o un problema de todas. Los errores del escáner ya traen su puesto — por eso
// ahí se ve "Puerta" o "Eva" y en el TPV no se veía nada.
export function reportTpvError({ headers, eventId, kind = 'js_error', message, route, context }) {
  setTimeout(() => {
    postTpv(headers, { type: 'error', event_id: eventId || null, device_id: deviceId(), kind: String(kind).slice(0, 40), message: clean(message), route: route ? clean(route) : null, context: context || null })
      .catch(() => {});   // el TPV no encola errores: la cola que importa es la de ventas
  }, 0);
}

export function reportTpvHeartbeat({ headers, eventId, metrics }) {
  postTpv(headers, { type: 'heartbeat', event_id: eventId || null, device_id: deviceId(), metrics: metrics || {} }).catch(() => {});
}

export const HEARTBEAT_MS = 60000;
